import React from 'react';
import { GaugeValor } from '../utils/auditoriaCockpit';

interface Props {
  rotulo: string;
  ajuda?: string;
  dado: GaugeValor;
  cor: string;
  formatarValor?: (valor: number) => string;
  // COR-272: quando informado, o card inteiro vira clicável — abre o modal listando tudo que
  // compõe o número exibido (ver AuditoriaCockpitDetalheModal / auditoriaCockpitDetalhe.ts).
  onClick?: () => void;
}

const TAMANHO = 88;
const CENTRO = TAMANHO / 2;
const RAIO = 34;
const ESPESSURA = 6;

// Velocímetro de verdade: arco de 270° (não um círculo fechado) partindo de 135° (canto inferior
// esquerdo) até 45° (canto inferior direito), passando pelo topo — deixa um "vão" de 90° embaixo
// onde o número fica, sem cruzar o ponteiro (que gira só pelos outros 270°, nunca por baixo).
const ANGULO_INICIO = 135;
const ANGULO_VARREDURA = 270;
const COMPRIMENTO_ARCO = RAIO * ((ANGULO_VARREDURA * Math.PI) / 180);

function pontoEmAngulo(anguloGraus: number, raio: number): { x: number; y: number } {
  const anguloRad = (anguloGraus * Math.PI) / 180;
  return { x: CENTRO + raio * Math.cos(anguloRad), y: CENTRO + raio * Math.sin(anguloRad) };
}

function anguloDoPercentual(percentual: number): number {
  return ANGULO_INICIO + (percentual / 100) * ANGULO_VARREDURA;
}

const pontoInicioArco = pontoEmAngulo(ANGULO_INICIO, RAIO);
const pontoFimArco = pontoEmAngulo(ANGULO_INICIO + ANGULO_VARREDURA, RAIO);
const CAMINHO_ARCO = `M ${pontoInicioArco.x} ${pontoInicioArco.y} A ${RAIO} ${RAIO} 0 1 1 ${pontoFimArco.x} ${pontoFimArco.y}`;

// Tracinhos de escala ao redor do arco (a cada 20%), do lado de FORA do traço — puramente
// decorativo, igual ao velocímetro de referência.
const MARCAS_PERCENTUAL = [0, 20, 40, 60, 80, 100];

// Gauge circular do cockpit da aba "TODOS" — SVG próprio, estilo velocímetro: arco de 270° (não
// fechado), ponteiro real (linha do centro até a ponta + dot), tracinhos de escala por fora, e o
// número como texto HTML normal posicionado no "vão" de baixo (nunca cruza o ponteiro).
const AuditoriaGauge: React.FC<Props> = ({ rotulo, ajuda, dado, cor, formatarValor, onClick }) => {
  const percentual = dado.max > 0 ? Math.min(100, (dado.valor / dado.max) * 100) : 0;
  const texto = formatarValor ? formatarValor(dado.valor) : String(dado.valor);
  const textoMax = formatarValor ? formatarValor(dado.max) : String(dado.max);
  const dashoffset = COMPRIMENTO_ARCO * (1 - percentual / 100);
  const anguloAtual = anguloDoPercentual(percentual);
  const ponteiro = pontoEmAngulo(anguloAtual, RAIO - 2);

  const ajudaCompleta = onClick ? `${ajuda ?? ''} Clique para ver a lista detalhada.`.trim() : ajuda;

  return (
    <div
      className={`auditoria-cockpit-gauge${onClick ? ' auditoria-cockpit-gauge-clicavel' : ''}`}
      style={{ borderTopColor: cor }}
      title={ajudaCompleta}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className='auditoria-cockpit-gauge-rotulo'>{rotulo}</div>
      <div className='auditoria-cockpit-gauge-dial'>
        <svg viewBox={`0 0 ${TAMANHO} ${TAMANHO}`} width='100%' height='100%'>
          <path d={CAMINHO_ARCO} fill='none' stroke='#eef0f4' strokeWidth={ESPESSURA} strokeLinecap='round' />
          {percentual > 0 && (
            <path
              d={CAMINHO_ARCO}
              fill='none'
              stroke={cor}
              strokeWidth={ESPESSURA}
              strokeLinecap='round'
              strokeDasharray={COMPRIMENTO_ARCO}
              strokeDashoffset={dashoffset}
            />
          )}
          {MARCAS_PERCENTUAL.map((marca) => {
            const anguloMarca = anguloDoPercentual(marca);
            const pInterno = pontoEmAngulo(anguloMarca, RAIO + ESPESSURA / 2 + 1);
            const pExterno = pontoEmAngulo(anguloMarca, RAIO + ESPESSURA / 2 + 4);
            return (
              <line
                key={marca}
                x1={pInterno.x} y1={pInterno.y} x2={pExterno.x} y2={pExterno.y}
                stroke='#d1d5db' strokeWidth={1.2}
              />
            );
          })}
          {/* Ponteiro: linha curta do centro até a ponta do valor atual + dot na ponta — só varre
              os 270° do arco, nunca o vão de baixo onde o número fica. */}
          <line x1={CENTRO} y1={CENTRO} x2={ponteiro.x} y2={ponteiro.y} stroke={cor} strokeWidth={2} />
          <circle cx={ponteiro.x} cy={ponteiro.y} r={3} fill={cor} />
          <circle cx={CENTRO} cy={CENTRO} r={2.2} fill={cor} />
        </svg>
        <div className='auditoria-cockpit-gauge-valor' style={{ color: cor }}>{texto}</div>
      </div>
      <div className='auditoria-cockpit-gauge-max'>de {textoMax}</div>
    </div>
  );
};

export default AuditoriaGauge;
